// <copyright company="Microsoft">Copyright (c) Microsoft. All rights reserved.</copyright>

import React, { useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
    Badge, Button, Card, Checkbox, Field, Input, MessageBar, MessageBarBody, Spinner,
    Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow,
    Title1, Title3, Text, makeStyles, shorthands, tokens,
} from '@fluentui/react-components';
import {
    ArrowDownRegular, ArrowUpRegular, CheckmarkCircleRegular, ClockRegular, DocumentSearchRegular,
    InfoRegular, LinkRegular, LockClosedRegular, ProhibitedRegular, SearchRegular, WarningRegular,
} from '@fluentui/react-icons';
import { usePageTitle } from '../../hooks/usePageTitle';
import { acquireAccessToken } from '../../utils/authToken';
import { CfdiLookupResult, RelatedCfdi, SUPPORTED_ISSUER_RFC, isCfdiUuid, lookupCfdi } from './cfdiLookupService';
import CfdiRelationships from './CfdiRelationships';
import CfdiUuidActions from './CfdiUuidActions';

type StatusField = keyof NonNullable<CfdiLookupResult['status']>;
type StatusColor = 'success' | 'warning' | 'danger' | 'brand' | 'subtle';

const STATUS_PRESENTATION: Record<StatusField, Partial<Record<string, { color: StatusColor; label: string }>>> = {
    documentStatus: {
        vigente: { color: 'success', label: 'Active CFDI' },
        cancelado: { color: 'subtle', label: 'Canceled CFDI' },
        'no encontrado': { color: 'warning', label: 'Not found by GoSocket' },
    },
    cancellability: {
        'cancelable sin aceptacion': { color: 'success', label: 'Cancellation permitted' },
        'cancelable con aceptacion': { color: 'warning', label: 'Recipient acceptance required' },
        'no cancelable': { color: 'danger', label: 'Cancellation blocked' },
    },
    cancellationStatus: {
        'en proceso': { color: 'brand', label: 'Cancellation request pending' },
    },
};

const STATUS_ICONS: Record<StatusColor, React.ReactElement> = {
    success: <CheckmarkCircleRegular aria-hidden="true" />,
    warning: <WarningRegular aria-hidden="true" />,
    danger: <ProhibitedRegular aria-hidden="true" />,
    brand: <ClockRegular aria-hidden="true" />,
    subtle: <InfoRegular aria-hidden="true" />,
};

function normalizeStatus(value: string | null): string {
    return (value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase().replace(/\s+/g, ' ');
}

const useStyles = makeStyles({
    root: {
        padding: '32px', backgroundColor: tokens.colorNeutralBackground2, minHeight: '100%',
        boxSizing: 'border-box', minWidth: 0,
        '@media (max-width: 640px)': { padding: '16px' },
    },
    content: {
        display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '1240px',
        marginLeft: 'auto', marginRight: 'auto', minWidth: 0,
    },
    header: { display: 'flex', flexDirection: 'column', gap: '12px' },
    headingRow: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px' },
    heading: { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 },
    pageTitle: { margin: 0, '@media (max-width: 640px)': { fontSize: '26px', lineHeight: '32px' } },
    heroIcon: {
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        width: '52px', height: '52px', fontSize: '28px',
        backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground2,
        ...shorthands.borderRadius(tokens.borderRadiusXLarge),
    },
    muted: { color: tokens.colorNeutralForeground3 },
    subtitle: { color: tokens.colorNeutralForeground2, maxWidth: '90ch' },
    card: {
        display: 'flex', flexDirection: 'column', gap: '16px', padding: '24px', minWidth: 0,
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
        ...shorthands.borderRadius(tokens.borderRadiusXLarge),
        boxShadow: tokens.shadow2,
        '@media (max-width: 640px)': { padding: '16px' },
    },
    sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' },
    sectionTitle: { margin: 0 },
    form: { display: 'flex', flexWrap: 'wrap', alignItems: 'end', gap: '12px' },
    input: { flexGrow: 1, flexBasis: '360px', minWidth: 0, maxWidth: '640px' },
    inputControl: { minWidth: 0, width: '100%', boxSizing: 'border-box', fontFamily: tokens.fontFamilyMonospace },
    options: {
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px',
        paddingTop: '12px', ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    },
    accessNote: { display: 'flex', alignItems: 'flex-start', gap: '8px', color: tokens.colorNeutralForeground3 },
    accessIcon: { flexShrink: 0, marginTop: '2px', fontSize: '16px' },
    resultMeta: {
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap',
        gap: '12px', paddingBottom: '16px', ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    },
    metaGroup: { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 },
    statusGrid: {
        display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '12px', margin: 0,
        '@media (min-width: 641px) and (max-width: 900px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
        '@media (max-width: 640px)': { gridTemplateColumns: 'minmax(0, 1fr)' },
    },
    statusItem: {
        display: 'flex', flexDirection: 'column', gap: '8px', padding: '16px',
        backgroundColor: tokens.colorNeutralBackground2, ...shorthands.borderRadius(tokens.borderRadiusLarge),
        minWidth: 0,
    },
    statusLabel: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
    statusValue: {
        margin: 0, fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400,
        fontWeight: tokens.fontWeightSemibold, overflowWrap: 'anywhere',
    },
    statusBadge: {
        maxWidth: '100%', height: 'auto', minHeight: '28px', boxSizing: 'border-box',
        whiteSpace: 'normal', textAlign: 'left', overflowWrap: 'anywhere',
        ...shorthands.padding('6px', '10px'),
    },
    relationshipSection: { display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 },
    relationshipGrid: {
        display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px',
        '@media (max-width: 900px)': { gridTemplateColumns: 'minmax(0, 1fr)' },
    },
    relationshipHeading: { display: 'flex', alignItems: 'center', gap: '8px' },
    relationshipIcon: { color: tokens.colorBrandForeground2, fontSize: '20px', flexShrink: 0 },
    emptyState: {
        display: 'flex', alignItems: 'center', gap: '12px', padding: '20px',
        backgroundColor: tokens.colorNeutralBackground2, color: tokens.colorNeutralForeground3,
        ...shorthands.borderRadius(tokens.borderRadiusLarge),
    },
    table: { overflowX: 'auto', minWidth: 0 },
    tableContent: { width: '100%', tableLayout: 'fixed' },
    uuid: { fontFamily: tokens.fontFamilyMonospace, overflowWrap: 'anywhere' },
    summary: {
        cursor: 'pointer', fontWeight: tokens.fontWeightSemibold, color: tokens.colorBrandForeground1,
        paddingTop: '4px', paddingBottom: '4px', overflowWrap: 'anywhere',
    },
    diagnosticContent: { display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px', minWidth: 0 },
    diagnosticSection: { display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 },
    diagnostics: {
        whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontFamily: tokens.fontFamilyMonospace,
        fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase300, margin: 0, padding: '16px',
        maxHeight: '320px', overflow: 'auto', backgroundColor: tokens.colorNeutralBackground2,
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
        ...shorthands.borderRadius(tokens.borderRadiusLarge),
    },
});

export default function CfdiLookup() {
    usePageTitle('Mexico CFDI Lookup');
    const styles = useStyles();
    const { instance, accounts } = useMsal();
    const [uuid, setUuid] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState<CfdiLookupResult | null>(null);
    const [includeDiagnostics, setIncludeDiagnostics] = useState(false);
    const [diagnosticsRequested, setDiagnosticsRequested] = useState(false);
    const request = useRef<AbortController | null>(null);

    useEffect(() => () => request.current?.abort(), []);

    function statusBadge(field: StatusField, value: string | null, fallback: string) {
        const normalized = normalizeStatus(value);
        const presentation = STATUS_PRESENTATION[field][normalized];
        const color = presentation?.color ?? 'subtle';
        return <Badge appearance="tint" size="extra-large" color={color} className={styles.statusBadge}
            icon={STATUS_ICONS[color]} title={presentation?.label ?? (normalized ? 'Status as reported by GoSocket' : 'No status returned')}>
            {value || fallback}
        </Badge>;
    }

    async function consult(value: string) {
        if (request.current) return;
        const normalizedUuid = value.trim().toLowerCase();
        setResult(null);
        setError('');
        setUuid(normalizedUuid);
        setDiagnosticsRequested(includeDiagnostics);
        if (!isCfdiUuid(normalizedUuid)) {
            setError('Enter a valid CFDI UUID in 8-4-4-4-12 format.');
            return;
        }
        const controller = new AbortController();
        request.current = controller;
        setBusy(true);
        try {
            const token = await acquireAccessToken(instance, accounts[0]);
            if (controller.signal.aborted) return;
            if (!token) {
                setError('Sign-in is in progress. Try again after signing in.');
                return;
            }
            const response = await lookupCfdi(normalizedUuid, token, controller.signal, includeDiagnostics);
            if (!controller.signal.aborted) setResult(response);
        } catch (lookupError) {
            if (!controller.signal.aborted) {
                setError(lookupError instanceof Error ? lookupError.message : 'CFDI lookup failed. Contact e-Invoicing support.');
            }
        } finally {
            if (!controller.signal.aborted) setBusy(false);
            request.current = null;
        }
    }

    function relationships(title: string, direction: 'parent' | 'child', documents: RelatedCfdi[]) {
        return <Card className={styles.card}>
            <div className={styles.sectionHeader}>
                <div className={styles.relationshipHeading}>
                    {direction === 'parent' ? <ArrowUpRegular aria-hidden="true" className={styles.relationshipIcon} /> :
                        <ArrowDownRegular aria-hidden="true" className={styles.relationshipIcon} />}
                    <Title3 as="h3" className={styles.sectionTitle}>{title}</Title3>
                </div>
                <Badge appearance="tint" color="subtle" aria-label={`${documents.length} ${direction} CFDIs`}>
                    {documents.length}
                </Badge>
            </div>
            <Text size={200} className={styles.muted}>CFDIs listed as {direction === 'parent' ? 'parents' : 'children'} by GoSocket.</Text>
            {documents.length === 0 ? <div className={styles.emptyState}>
                <LinkRegular aria-hidden="true" fontSize={24} />
                <Text>No {direction} CFDIs returned for this CFDI.</Text>
            </div> :
                <div className={styles.table}>
                    <Table aria-label={title} className={styles.tableContent}>
                        <TableHeader><TableRow>
                            <TableHeaderCell>UUID</TableHeaderCell>
                        </TableRow></TableHeader>
                        <TableBody>{documents.map((document, index) =>
                            <TableRow key={`${document.uuid}-${index}`}><TableCell>
                                <CfdiUuidActions document={document} busy={busy} onConsult={consult} />
                            </TableCell></TableRow>
                        )}</TableBody>
                    </Table>
                </div>}
        </Card>;
    }

    return <div className={styles.root}><div className={styles.content}>
        <header className={styles.header}>
            <div className={styles.headingRow}>
                <div className={styles.heroIcon}><DocumentSearchRegular aria-hidden="true" /></div>
                <div className={styles.heading}>
                    <Text size={200} weight="semibold" className={styles.muted}>MICROSOFT MEXICO</Text>
                    <Title1 as="h1" className={styles.pageTitle}>Mexico CFDI Lookup</Title1>
                </div>
                <Badge appearance="tint" color="subtle" icon={<LockClosedRegular aria-hidden="true" />}>Read-only</Badge>
            </div>
            <Text className={styles.subtitle}>Check document status, cancellation details and related CFDIs directly with GoSocket / SAT.
                You can also look up older CFDIs that are not listed in this dashboard.</Text>
        </header>
        <Card className={styles.card}>
            <Title3 as="h2" className={styles.sectionTitle}>Consult a CFDI</Title3>
            <form className={styles.form} onSubmit={event => { event.preventDefault(); void consult(uuid); }}>
                <Field label="CFDI UUID" className={styles.input}>
                    <Input size="large" className={styles.inputControl} value={uuid} disabled={busy} onChange={(_, data) => {
                        setUuid(data.value);
                        setResult(null);
                        setError('');
                    }} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                </Field>
                <Button type="submit" size="large" appearance="primary" icon={<SearchRegular aria-hidden="true" />}
                    disabled={busy || !uuid.trim()}>Consult CFDI</Button>
            </form>
            <div className={styles.options}>
                <Checkbox label="Include raw supplier responses (temporary)" checked={includeDiagnostics} disabled={busy}
                    onChange={(_, data) => setIncludeDiagnostics(data.checked === true)} />
                <Text size={200} className={styles.muted}>Temporary troubleshooting only. Responses are unredacted and may contain RFCs and invoice data,
                    including related parties. Do not share them outside authorized support. No extra supplier calls are made.</Text>
            </div>
            <div className={styles.accessNote}>
                <LockClosedRegular aria-hidden="true" className={styles.accessIcon} />
                <Text size={200}>This is a read-only lookup for Microsoft Mexico. Mexico production access with All Partners,
                    or dashboard administrator access, is required.</Text>
            </div>
        </Card>
        {busy && <Spinner label="Consulting GoSocket..." />}
        {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
        {result && <>
            <div className={styles.resultMeta}>
                <div className={styles.metaGroup}>
                    <Text weight="semibold">Consultation result</Text>
                    <Text className={styles.uuid}>{result.uuid}</Text>
                </div>
                <div className={styles.metaGroup}>
                    <Text size={200} className={styles.muted}>Source: GoSocket / SAT consultation</Text>
                    <Text size={200} className={styles.muted}>Retrieved {new Date(result.retrievedAtUtc).toLocaleString()}</Text>
                </div>
            </div>
            <MessageBar intent="info"><MessageBarBody>
                This view does not cancel, unlink or reprocess invoices. Parent/child relationships do not by themselves
                prove which CFDI blocks cancellation or determine cancellation order. Vendor data may lag SAT.
            </MessageBarBody></MessageBar>
            {result.statusError ?
                <MessageBar intent="warning"><MessageBarBody>Status unavailable: {result.statusError}</MessageBarBody></MessageBar> :
                result.status && <Card className={styles.card}>
                    <Title3 as="h2" className={styles.sectionTitle}>CFDI status</Title3>
                    <dl className={styles.statusGrid} aria-label="CFDI status details">
                        <div className={styles.statusItem}>
                            <dt className={styles.statusLabel}>Document status</dt>
                            <dd className={styles.statusValue}>{statusBadge('documentStatus', result.status.documentStatus, 'Not returned')}</dd>
                        </div>
                        <div className={styles.statusItem}>
                            <dt className={styles.statusLabel}>Cancellability</dt>
                            <dd className={styles.statusValue}>{statusBadge('cancellability', result.status.cancellability, 'Not returned')}</dd>
                        </div>
                        <div className={styles.statusItem}>
                            <dt className={styles.statusLabel}>Cancellation request status</dt>
                            <dd className={styles.statusValue}>{statusBadge('cancellationStatus', result.status.cancellationStatus, 'No cancellation state returned')}</dd>
                        </div>
                    </dl>
                    {normalizeStatus(result.status.documentStatus) === 'no encontrado' &&
                        <Text>GoSocket did not find this CFDI. This does not establish whether it exists at SAT.</Text>}
                </Card>}
            {result.relationshipsError ?
                <MessageBar intent="warning"><MessageBarBody>Relationships unavailable: {result.relationshipsError}</MessageBarBody></MessageBar> :
                result.relationships && (result.relationships.code === '2001' ?
                    <Text className={styles.muted}>GoSocket reported no related CFDIs (code 2001).</Text> :
                    <section className={styles.relationshipSection} aria-label="Related CFDIs">
                    <div className={styles.heading}>
                        <Title3 as="h2" className={styles.sectionTitle}>Related CFDIs</Title3>
                        <Text className={styles.muted}>Parent and child references returned by GoSocket.</Text>
                    </div>
                    <CfdiRelationships key={`${result.uuid}-${result.retrievedAtUtc}`} uuid={result.uuid}
                        parents={result.relationships.parents} children={result.relationships.children} busy={busy} onConsult={consult}
                        list={<div className={styles.relationshipGrid}>
                            {relationships('Parent CFDIs', 'parent', result.relationships.parents)}
                            {relationships('Child CFDIs', 'child', result.relationships.children)}
                        </div>} />
                    <Text size={200} className={styles.muted}>Select a UUID to consult it, or use its copy button without starting a lookup.
                        Only UUIDs issued by {SUPPORTED_ISSUER_RFC} can be consulted; other UUIDs remain copyable. No automatic traversal or polling is performed.</Text>
                </section>)}
            {result.diagnostics && <Card className={styles.card}>
                <details>
                    <summary className={styles.summary}>Raw supplier responses (temporary troubleshooting)</summary>
                    <div className={styles.diagnosticContent}>
                        <Text className={styles.muted}>Unredacted document API bodies captured before parsing, up to 65,536 characters per response.
                            OAuth responses are not captured. Raw content does not establish whether relationships exist.
                            Older backend regions may still return redacted snapshots.</Text>
                        {[
                            { name: 'GetStatusDocument', response: result.diagnostics.status },
                            { name: 'GetRelatedDocuments', response: result.diagnostics.relationships },
                        ].map(({ name, response }) => <section key={name} className={styles.diagnosticSection}>
                            <Text weight="semibold">{name}</Text>
                            <Text>HTTP: {response.httpStatusCode ?? 'No response'}; validation stage: {response.validationStage}</Text>
                            <pre role="region" tabIndex={0} aria-label={`${name} raw response`}
                                className={styles.diagnostics}>{response.rawBody ?? response.redactedBody ?? 'No document API response was captured.'}</pre>
                        </section>)}
                        <Text size={200} className={styles.muted}>A no-response stage means no document API response was captured.</Text>
                    </div>
                </details>
            </Card>}
            {diagnosticsRequested && !result.diagnostics && <MessageBar intent="info"><MessageBarBody>
                Supplier diagnostics were not returned. The backend region may not yet have the diagnostics update deployed.
            </MessageBarBody></MessageBar>}
        </>}
    </div></div>;
}
